import React, { useState } from 'react';
import { Loader2, ShieldCheck, Smartphone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/ui-kit/button';
import { Input } from '@/ui-kit/input';
import { Label } from '@/ui-kit/label';
import { useAuth } from '@/context/AuthContext';
import {
    enrollTotpFactor,
    totpQrCodeDataUrl,
    verifyTotpEnrollment,
} from '@/services/authSecurity';
import { toast } from 'sonner';

type SecurityEnrollmentScreenProps = {
    onComplete: () => void;
};

export const SecurityEnrollmentScreen: React.FC<SecurityEnrollmentScreenProps> = ({ onComplete }) => {
    const { logout } = useAuth();
    const navigate = useNavigate();
    const [isEnrollingTotp, setIsEnrollingTotp] = useState(false);
    const [isVerifyingTotp, setIsVerifyingTotp] = useState(false);
    const [pendingFactorId, setPendingFactorId] = useState<string | null>(null);
    const [qrCode, setQrCode] = useState('');
    const [secret, setSecret] = useState('');
    const [verificationCode, setVerificationCode] = useState('');

    const handleSignOut = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error('Sign out failed:', error);
            toast.error('Failed to sign out. Please try again.');
        }
    };

    const handleStartTotp = async () => {
        setIsEnrollingTotp(true);
        try {
            const enrollment = await enrollTotpFactor();
            setPendingFactorId(enrollment.factorId);
            setQrCode(enrollment.qrCode);
            setSecret(enrollment.secret);
        } catch (error) {
            const message =
                error instanceof Error ? error.message : 'Failed to start authenticator setup.';
            toast.error(message);
        } finally {
            setIsEnrollingTotp(false);
        }
    };

    const handleVerifyTotp = async () => {
        if (!pendingFactorId) return;
        setIsVerifyingTotp(true);
        try {
            await verifyTotpEnrollment(pendingFactorId, verificationCode.trim());
            toast.success('Authenticator app enabled.');
            onComplete();
        } catch (error) {
            const message =
                error instanceof Error ? error.message : 'Verification code is invalid.';
            toast.error(message);
        } finally {
            setIsVerifyingTotp(false);
        }
    };

    const showSetupForm = Boolean(pendingFactorId && (qrCode || secret));

    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
            <div className="w-full max-w-lg rounded-2xl border border-border bg-card p-8 shadow-lg">
                <div className="mb-6 flex items-start gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <ShieldCheck className="h-5 w-5" />
                    </div>
                    <div>
                        <h1 className="text-xl font-semibold text-foreground">
                            Secure your account
                        </h1>
                        <p className="mt-1 text-sm text-muted-foreground">
                            Before continuing, set up an authenticator app for this account.
                        </p>
                    </div>
                </div>

                {!showSetupForm ? (
                    <div className="space-y-4">
                        <div className="rounded-lg border border-border p-4 space-y-3">
                            <div className="flex items-center gap-2 text-foreground">
                                <Smartphone className="h-4 w-4" />
                                <h2 className="font-medium">Authenticator app</h2>
                            </div>
                            <p className="text-sm text-muted-foreground">
                                Use Google Authenticator, 1Password, or another TOTP app.
                            </p>
                            <Button
                                type="button"
                                className="w-full"
                                onClick={() => void handleStartTotp()}
                                disabled={isEnrollingTotp}
                            >
                                {isEnrollingTotp ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Preparing...
                                    </>
                                ) : (
                                    'Set up authenticator'
                                )}
                            </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            You can manage this method later in My Account.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Scan this QR code with your authenticator app, then enter the 6-digit code
                            to continue.
                        </p>
                        {qrCode ? (
                            <img
                                src={totpQrCodeDataUrl(qrCode)}
                                alt="Authenticator app QR code"
                                className="h-44 w-44 max-w-full rounded-md border border-border bg-white p-2 object-contain"
                            />
                        ) : null}
                        {secret ? (
                            <div className="space-y-1">
                                <Label htmlFor="enrollment-totp-secret">Manual setup key</Label>
                                <p
                                    id="enrollment-totp-secret"
                                    className="rounded-md border border-border bg-muted px-3 py-2 font-mono text-xs break-all"
                                >
                                    {secret}
                                </p>
                            </div>
                        ) : null}
                        <div className="space-y-2">
                            <Label htmlFor="enrollment-totp-code">Verification code</Label>
                            <Input
                                id="enrollment-totp-code"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                placeholder="123456"
                                value={verificationCode}
                                onChange={(e) => setVerificationCode(e.target.value.trim())}
                            />
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Button
                                type="button"
                                onClick={() => void handleVerifyTotp()}
                                disabled={isVerifyingTotp || verificationCode.length < 6}
                            >
                                {isVerifyingTotp ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Verifying...
                                    </>
                                ) : (
                                    'Enable and continue'
                                )}
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setPendingFactorId(null);
                                    setQrCode('');
                                    setSecret('');
                                    setVerificationCode('');
                                }}
                            >
                                Back
                            </Button>
                        </div>
                    </div>
                )}

                <div className="mt-6 border-t border-border pt-4">
                    <Button type="button" variant="ghost" onClick={() => void handleSignOut()}>
                        Sign out
                    </Button>
                </div>
            </div>
        </div>
    );
};
